<template>
  <canvas :id="chartid" class="chart-canvas" />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUpdate, onBeforeUnmount } from "vue";
import Chart, {
  ChartConfiguration,
  ChartData,
  ChartDataset,
  ChartOptions,
  LinearScaleOptions,
  Scale,
} from "chart.js/auto";
import { useChart } from "@/composables/useChart";
import { useUtils } from "@/composables/useUtils";
import { Timestamp } from "@/types/Timestamp";

const { isCanvas } = useUtils();
const { getChartConfig, getChartOptions } = useChart();

const props = defineProps<{
  beforeUpdate: (scale: Scale<LinearScaleOptions>) => void;
  chartid: string;
  datasets: ChartDataset[];
  labels: Timestamp[];
  minTime: Timestamp;
  maxTime: Timestamp;
  minVal?: number;
  maxVal?: number;
  maxZoom: number;
  showDots: boolean;
}>();

// chartObject is emphatically NOT reactive:
// this would cause an infinite loop when an update triggers
let chartObject: Chart | undefined;

const chartElement = ref<HTMLElement>();

const chartData = computed<ChartData>(() => ({
  labels: props.labels,
  datasets: props.datasets,
}));

const chartOptions = computed<ChartOptions>(() => {
  const pointRadius = props.showDots ? 2 : 0;
  // ensure that undefined stays undefined in minVal and maxVal
  return getChartOptions(
    props.minTime,
    props.maxTime,
    props.minVal,
    props.maxVal,
    pointRadius,
  );
});

const chartConfig = computed<ChartConfiguration>(() =>
  getChartConfig(chartOptions.value, chartData.value, props.beforeUpdate),
);

onMounted(() => {
  chartElement.value = document.getElementById(props.chartid) || undefined;
  if (!isCanvas(chartElement.value)) {
    return;
  }

  chartObject = new Chart(chartElement.value, chartConfig.value);
});

onBeforeUpdate(() => {
  if (!chartObject) {
    return;
  }
  chartObject.data = chartData.value;
  chartObject.options = chartOptions.value;
  chartObject.update();
});

onBeforeUnmount(() => {
  chartObject?.destroy();
});
</script>

<style scoped lang="scss">
.chart-canvas {
  position: relative;
  height: 50vh;
  padding: 5px;
}
</style>
